import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import ui from "../../pages/dashboard/DashboardUI.module.css";
import Icon from "../common/Icon";
import RowActionsMenu from "../common/RowActionsMenu";
import styles from "./DetailsModal.module.css";

/**
 * DetailsModal - A details view modal, either read-only (Edit button opens a
 * separate form) or inline-editable (titleBar + menuActions, no footer).
 *
 * Props:
 * - title: Modal title
 * - onClose: Callback when closing the modal
 * - onEdit: Callback when Edit button is clicked (if null, Edit button is hidden)
 * - onDelete: Callback when Delete button is clicked (if null, Delete button is hidden)
 * - children: Content to display in the modal body
 * - tabs: Optional array of tab objects { key, label } for tabbed content
 * - activeTab: Current active tab key (controlled)
 * - onTabChange: Callback when tab changes
 * - sidePanel: Optional React node (e.g. <ActivityPanel />) rendered as a
 *   second column to the right of the tabbed content. When present, the
 *   modal widens to fit both columns; when absent, layout is unchanged.
 * - titleBar: Optional node rendered as a title band above the tabs (record
 *   name, client, key properties). Opts the modal into the inline-editing
 *   layout: the redundant footer is dropped, since nothing there is needed
 *   once every field saves itself.
 * - accentColor: Colour of the thin bar down the left of the title band.
 * - menuActions: Actions for the "⋯" overflow menu next to the close button
 *   ({ label, onClick, danger }[]) — where destructive actions belong once
 *   there's no footer.
 */
export default function DetailsModal({
  title,
  onClose,
  onEdit,
  onDelete,
  children,
  tabs = null,
  activeTab = null,
  onTabChange = null,
  deleteLabel = "Delete",
  editLabel = "Edit",
  sidePanel = null,
  titleBar = null,
  accentColor = null,
  menuActions = [],
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // With an inline-editing title bar there is nothing left for a footer to do:
  // every field commits itself, Delete moves into the overflow menu, and Close
  // duplicates the × in the corner.
  const showFooter = Boolean(onEdit || onDelete || !titleBar);

  const tabBar =
    tabs && tabs.length > 0 ? (
      <div className={styles.tabContainer}>
        {tabs.map((tab) => {
          const tabKey = typeof tab === 'string' ? tab : tab.key;
          const tabLabel = typeof tab === 'string' ? tab : tab.label;

          return (
            <button
              key={tabKey}
              type="button"
              className={`${styles.tabButton} ${
                activeTab === tabKey ? styles.activeTab : ""
              }`}
              onClick={() => onTabChange?.(tabKey)}
            >
              {tabLabel}
            </button>
          );
        })}
      </div>
    ) : (
      <div className={styles.tabContainer} />
    );

  const cornerControls = (
    <div className={styles.cornerControls}>
      {menuActions.length > 0 && <RowActionsMenu actions={menuActions} />}
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label="Close"
      >
        <Icon name="close" size="1.05rem" />
      </button>
    </div>
  );

  return ReactDOM.createPortal(
    <div className={ui.modalOverlay}>
      <div className={`${ui.modal} ${styles.detailsModal} ${sidePanel ? styles.withSidePanel : ""}`}>
        {titleBar ? (
          <div className={styles.headerBand}>
            {/* One accent down the whole band, not just the title row */}
            <span
              className={styles.accentBar}
              style={accentColor ? { background: accentColor } : undefined}
            />
            <div className={styles.titleBar}>
              <div className={styles.titleBarContent}>{titleBar}</div>
              {cornerControls}
            </div>
            <div className={styles.header}>{tabBar}</div>
          </div>
        ) : (
          <div className={styles.header}>
            {tabBar}
            {cornerControls}
          </div>
        )}

        {/* Modal Body: main content + optional side panel */}
        <div className={sidePanel ? styles.bodyWithSidePanel : styles.content}>
          <div className={sidePanel ? styles.mainColumn : undefined}>
            {children}
          </div>
          {sidePanel && (
            <div className={styles.sidePanelColumn}>
              {sidePanel}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {showFooter && (
        <div className={styles.footer}>
          <div className={styles.leftActions}>
            {onDelete && (
              <button
                type="button"
                className={ui.buttonDanger}
                onClick={onDelete}
              >
                {deleteLabel}
              </button>
            )}
          </div>
          <div className={styles.rightActions}>
            <button
              type="button"
              className={ui.cancelButton}
              onClick={onClose}
            >
              Close
            </button>
            {onEdit && (
              <button
                type="button"
                className={ui.buttonPrimary}
                onClick={onEdit}
              >
                {editLabel}
              </button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}
